import React, { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import ServicePage from "@/pages/ServicePage";
import Contact from "@/pages/Contact";
import Admin from "@/pages/Admin";
import AreaPage from "@/pages/AreaPage";
import ReviewPage from "@/pages/ReviewPage";
import LegacyRedirect from "@/components/LegacyRedirect";
import "@/App.css";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error) {
    console.error("App error boundary:", error);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#080B10] px-6 text-center">
          <p className="font-display text-2xl font-bold text-white">Something went wrong</p>
          <p className="text-sm text-slate-400">Please refresh the page. If it persists, call us on 0485 032 854.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function LenisSetup() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
  return null;
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        const t = setTimeout(() => {
          if (window.__lenis) window.__lenis.scrollTo(el, { offset: -100 });
          else el.scrollIntoView({ behavior: "smooth" });
        }, 120);
        return () => clearTimeout(t);
      }
    }
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function Layout() {
  return (
    <>
      <LenisSetup />
      <ScrollToTop />
      <Nav />
      <Routes>
        <Route path="/" element={<><Home /><Footer /></>} />
        <Route path="/services/:slug" element={<><ServicePage /><Footer /></>} />
          <Route path="/contact" element={<><Contact /><Footer /></>} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/areas/:slug" element={<><AreaPage /><Footer /></>} />
        <Route path="/review" element={<ReviewPage />} />
        <Route path="*" element={<LegacyRedirect />} />
      </Routes>
      <Toaster theme="dark" position="bottom-right" richColors />
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Layout />
      </BrowserRouter>
    </ErrorBoundary>
  );
}
